import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTranslation } from '../../i18n';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { getErrorMessage } from '../../utils/errorHandler';
import { formatDate } from '../file-browser/utils';
import { useFlightLinksQuery } from './flightLinks.queries';
import { useLinkFlightMutation } from './flightLinks.mutations';
import { organizationList, promptCandidate, type UploadedBin } from './flightLinkModel';
import { startEvidence } from './flightLinkCopy';

interface BinUploadMatchPromptProps {
  /** The `.bin` logs a batch just stored, asked about one at a time, in order. */
  uploads: UploadedBin[];
  /** Called when every upload has been answered or had nothing to ask. */
  onDone: () => void;
}

/**
 * "Is this the same flight?" right after a `.bin` upload (#1694, Jakob
 * 2026-11-02: fewer clicks).
 *
 * For each uploaded log it reads the flights the uploader can see on the log's
 * day and, when one starts close enough to be proposed, asks with two answers:
 * **No** / **Yes, these are the same flight**. Nothing is linked without the
 * Yes. A log with no proposal (no clock in its name, no flight that day) is
 * passed over silently; Files → right-click → Link to flight is the fallback
 * for those and for a No given too fast.
 *
 * It says who the log will reach before the Yes, because linking a log to a
 * shared flight hands it to that flight's grantees.
 */
export const BinUploadMatchPrompt: React.FC<BinUploadMatchPromptProps> = ({ uploads, onDone }) => {
  const { t } = useTranslation();
  const { showSuccess } = useErrorHandler();
  const [index, setIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const current = uploads[index];
  const query = useFlightLinksQuery(current?.objectId);
  const linkMutation = useLinkFlightMutation();

  // A new batch starts at its first log.
  useEffect(() => {
    setIndex(0);
    setError(null);
  }, [uploads]);

  const candidate = query.data ? promptCandidate(query.data.candidates) : null;
  const alreadyLinked = (query.data?.links.length ?? 0) > 0;
  const nothingToAsk =
    !!current && (query.isError || (query.isSuccess && (!candidate || alreadyLinked)));

  // Answered (Yes or No) or nothing to ask: on to the next log.
  const next = () => {
    setError(null);
    setIndex((i) => i + 1);
  };

  // Skip a log there is nothing to ask about, without a flash of a dialog. Keyed
  // on the log as well, so two skips in a row each advance once.
  useEffect(() => {
    if (nothingToAsk) setIndex((i) => i + 1);
  }, [nothingToAsk, current?.objectId]);

  useEffect(() => {
    if (uploads.length > 0 && index >= uploads.length) onDone();
  }, [index, uploads.length, onDone]);

  const open = !!current && !!candidate && !alreadyLinked && !nothingToAsk;

  const answerYes = async () => {
    if (!current || !candidate) return;
    setError(null);
    try {
      await linkMutation.mutateAsync({ objectId: current.objectId, flightId: candidate.flight_id });
      showSuccess(t('flightLinks.linked'));
      next();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const flightName =
    candidate?.log_basename ||
    t('flightLinks.flightOn', {
      serial: candidate?.drone_serial ?? '?',
      date: candidate?.flight_date ? formatDate(candidate.flight_date) : '?',
    });

  return (
    <Dialog open={open} onClose={next} maxWidth="sm" fullWidth data-testid="bin-upload-prompt">
      <DialogTitle>{t('flightLinks.promptTitle')}</DialogTitle>
      <DialogContent>
        {current && candidate && (
          <>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {t('flightLinks.promptQuestion', { bin: current.name, flight: flightName })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {startEvidence(t, candidate)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {candidate.shared_with.length > 0
                ? t('flightLinks.sharedWith', {
                    orgs: organizationList(candidate.shared_with, t('flightLinks.unknownOrg')),
                  })
                : t('flightLinks.notShared')}
            </Typography>
          </>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={next} disabled={linkMutation.isPending} data-testid="bin-upload-prompt-no">
          {t('flightLinks.promptNo')}
        </Button>
        <Button
          variant="contained"
          onClick={answerYes}
          disabled={linkMutation.isPending}
          data-testid="bin-upload-prompt-yes"
        >
          {t('flightLinks.promptYes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
